import { InputBase, Stack, Typography, useTheme } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";

interface DurationInputProps {
  hours: string | number;
  minutes: string | number;
  onHoursChange: (value: number) => void;
  onMinutesChange: (value: number) => void;
  disabled: boolean;
}

const MAX_HOURS_DIGITS = 4;
const MAX_HOURS_VALUE = 9999;
const MAX_MINUTES_DIGITS = 2;
const MAX_MINUTES_VALUE = 59;

/** Strip leading zeros except when the entire string is "0". */
const stripLeadingZeros = (raw: string) =>
  raw.replace(/^0+(?=\d)/, "");

export const DurationInput = memo(
  ({
    hours,
    minutes,
    onHoursChange,
    onMinutesChange,
    disabled,
  }: DurationInputProps) => {
    const theme = useTheme();

    // Local display state — source of truth for what the user sees. We can't
    // rely purely on the parent prop because React won't update the DOM when
    // the normalized numeric value matches the previous prop (e.g. typing "0"
    // into a 0-valued field leaves the raw "00" stuck in the DOM).
    const [hoursText, setHoursText] = useState(String(hours ?? 0));
    const [minutesText, setMinutesText] = useState(String(minutes ?? 0));

    // Keep local text in sync when the parent prop changes (e.g. form reset,
    // edit-mode load, or the "Do not track" toggle clearing values to 0).
    useEffect(() => {
      setHoursText(String(hours ?? 0));
    }, [hours]);
    useEffect(() => {
      setMinutesText(String(minutes ?? 0));
    }, [minutes]);

    const handleHoursChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Strip non-digits first so pasted text / letter keys can't produce
        // NaN through the Number() call below.
        const digits = e.target.value.replace(/\D/g, "");
        const raw = stripLeadingZeros(digits);
        if (raw.length > MAX_HOURS_DIGITS) return;
        const val = Math.min(MAX_HOURS_VALUE, Math.max(0, Number(raw || 0)));
        // Always reflect the clamped numeric string back into the display so
        // "00" / "0005" visually collapse. Emit the numeric value to parent.
        setHoursText(String(val));
        onHoursChange(val);
      },
      [onHoursChange],
    );

    const handleMinutesChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, "");
        const raw = stripLeadingZeros(digits);
        if (raw.length > MAX_MINUTES_DIGITS) return;
        const val = Math.min(MAX_MINUTES_VALUE, Math.max(0, Number(raw || 0)));
        setMinutesText(String(val));
        onMinutesChange(val);
      },
      [onMinutesChange],
    );

    const digitInputSx = {
      "& input": {
        ...theme.typography["body-small"],
        padding: 0,
        textAlign: "center",
        width: "32px",
        MozAppearance: "textfield",
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
      },
    } as const;

    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          border: `1px solid ${theme.palette.neutral["015"]}`,
          borderRadius: "6px",
          px: "8px",
          py: "4px",
          gap: "2px",
          width: "fit-content",
          bgcolor: disabled ? theme.palette.neutral["005"] : "transparent",
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <InputBase
          type="text"
          inputMode="numeric"
          value={hoursText}
          onChange={handleHoursChange}
          disabled={disabled}
          sx={digitInputSx}
          inputProps={{
            maxLength: MAX_HOURS_DIGITS,
            pattern: "[0-9]*",
          }}
        />
        <Typography variant="body-small" color={theme.palette.lightBg.low}>
          h
        </Typography>
        <Typography
          variant="body-small"
          color={theme.palette.lightBg.low}
          sx={{ mx: "2px" }}
        >
          :
        </Typography>
        <InputBase
          type="text"
          inputMode="numeric"
          value={minutesText}
          onChange={handleMinutesChange}
          disabled={disabled}
          sx={digitInputSx}
          inputProps={{
            maxLength: MAX_MINUTES_DIGITS,
            pattern: "[0-9]*",
          }}
        />
        <Typography variant="body-small" color={theme.palette.lightBg.low}>
          m
        </Typography>
      </Stack>
    );
  },
);

DurationInput.displayName = "DurationInput";
